import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import { IdentificationFormStepNames, IdentificationFormSteps } from '../../types';
import { ManagerInfo } from './components/ManagerInfo';
import { Box, Button, Divider, Typography } from '@mui/material';
import { styles } from '../../styles';
import { InfoTooltip } from '~/components/InfoTooltip/InfoTooltip';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox';
import { useAppDispatch } from '~/store/appDispatch';
import { useManagmentStructureForm } from './hooks/useManagmentStructureForm';
import { FormProvider } from 'react-hook-form';
import { TManagmentStructure } from './types';
import { GoverningPersons } from './components/GoverningPersons';
import { updateFormData, updateFormDataBack } from '~/store/identification-form/slices';
import { getIsAssetManagersChanged, updateForm } from '~/store/identification-form/thunks/thunks';
import { B2fullCheck, trimValues } from '../../utils';
import { EMPTY_GOVERNING_PERSON, testDataManagementStructure } from './constants';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { InformationMessageBlock } from '~/modules/identification/components/InformationMessage/InformationMessageBlock.tsx';
import { infoMessages } from '~/constants/infoMessages.ts';
import { TIdentificationForm } from '~/store/identification-form/types';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext';
import { commonStyles } from '~/theme/styles/commonStyles';
import {
  selectApplicationId,
  selectIsReidentification,
} from '~/store/identification-form/selectors';
import { onInvalidForm } from '~/helpers/utils';
import { SetDirtyModalValues } from '../../components/SetDirtyModalValues';
import { getManagementStructureSyncAssetManagers } from '~/store/identification-form/thunks/assetManagersThunk.ts';
import { hasAssetManagersSyncFields } from '~/modules/identification/forms/ManagersAndRepresentatives/utils.ts';

interface IManagementStructureProps {
  onNextStep: () => void;
}

export const ManagementStructure = ({ onNextStep }: IManagementStructureProps) => {
  const dispatch = useAppDispatch();
  const appId = useSelector(selectApplicationId);
  const isReidentification = useSelector(selectIsReidentification);
  const { isTestEnvironmentEnabled } = useFeatureFlags();
  const form = useManagmentStructureForm();

  const { isDirty, isSubmitting, dirtyFields } = form.formState;

  const handleFillTestData = () =>
    form.reset(testDataManagementStructure, { keepDefaultValues: true });

  const onSubmit =
    (isDirty: boolean, disableGoNext?: boolean) => async (values: TManagmentStructure) => {
      if (isDirty) {
        const data = {
          managementStructure: trimValues(values),
          applicationId: appId as number,
        };

        try {
          await dispatch(
            updateForm({ data, reportId: IdentificationFormSteps.managementStructure })
          ).unwrap();
          const formData = {
            field: IdentificationFormStepNames.managementStructure as keyof TIdentificationForm,
            data: cloneDeep(values),
          };
          dispatch(updateFormData(formData));
          dispatch(updateFormDataBack(formData));
          B2fullCheck(appId || '', IdentificationFormSteps.managementStructure);
          dispatch(getIsAssetManagersChanged({ applicationId: appId as number }));
          if (hasAssetManagersSyncFields(dirtyFields)) {
            await dispatch(
              getManagementStructureSyncAssetManagers({ applicationId: appId! })
            ).unwrap();
          }

          !disableGoNext && onNextStep();
        } catch (error) {
          console.error(error);
        }
      } else {
        !disableGoNext && onNextStep();
      }
    };

  const handleCheck = (isChecked: boolean) => {
    form.setValue('governingPersons', isChecked ? [] : [EMPTY_GOVERNING_PERSON]);
  };

  return (
    <FormProvider {...form}>
      <Box
        component="form"
        onSubmit={form.handleSubmit(onSubmit(isDirty), onInvalidForm)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      >
        {isTestEnvironmentEnabled && (
          <Box sx={styles.testEnvironmentButton}>
            <Button variant={'outlined'} onClick={handleFillTestData}>
              Заповнити даними
            </Button>
          </Box>
        )}
        <Box sx={{ ...commonStyles.columnFlexContainer, gap: 3 }}>
          {isReidentification && (
            <InformationMessageBlock informationText={infoMessages.RE_VERIFICATION_MESSAGE} />
          )}
          <Typography variant="h5" sx={{ textAlign: 'center' }}>
            Відомості про керівника/особу, на яку покладені функції з керівництва та управління
            господарською діяльністю
          </Typography>
          <ManagerInfo />
          <Box sx={{ mt: 2, mb: 2 }}>
            <Divider sx={styles.divider} />
          </Box>
          <Box sx={{ ...commonStyles.centerFlexContainer, gap: 1 }}>
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              Надайте інформацію щодо інших осіб, які входять до складу органів управління
              (наглядова рада, правління, дирекція, тощо) або підтвердіть їх відсутність
            </Typography>
            <InfoTooltip
              title={`Зазначити відомості про осіб, які входять до складу Наглядової ради / Правління/
                    Дирекції, тощо`}
            />
          </Box>
          <FormCheckbox
            label="Відсутні інші особи, які входять до складу органів управління"
            name="hasNoGoverningPersons"
            onChange={handleCheck}
          />
          <GoverningPersons />

          <Button
            disabled={isSubmitting}
            type="submit"
            variant="contained"
            sx={{ alignSelf: 'center' }}
          >
            Продовжити
          </Button>
        </Box>
        <SetDirtyModalValues
          saveValues={form.handleSubmit(onSubmit(isDirty, true), onInvalidForm)}
        />
      </Box>
      {isSubmitting && <FullPageLoader />}
    </FormProvider>
  );
};
